import { keyframes, type CSSObject } from "@emotion/react";

import { BoxShadows, Colors, fontMedium, Icon, type IconName } from "~foundations";
import type { ValuesOf } from "~utils";

export type ButtonProps = {
  /**
   * The variant of the button
   * @default "primary"
   */
  type?: "primary" | "secondary" | "tertiary";
  /**
   * The button copy
   * @default undefined
   */
  children?: string;
  /**
   * The function to be called when the button is clicked
   * @required
   */
  onClick: () => void;
  /**
   * When true, the button will be 100% width
   * When false (default), the button width adjusts to its content.
   * @default false
   */
  isFluid?: boolean;
  /**
   * Button status determines interactive behavior and visuals.
   * - "enabled": clickable and active
   * - "loading": shows spinner and disables interaction
   * - "disabled": grayed out, non-interactive
   * @default "enabled"
   */
  status?: "enabled" | "loading" | "disabled";
  /**
   * The icon to be displayed in the button.
   * If provided, the icon will render to the left of the label by default.
   * Use `position: "right"` to place the icon on the right.
   *
   * @default undefined
   */
  icon?: {
    name: IconName;
    position?: "left" | "right";
  };
  /**
   * The data-testid attribute to be used for testing.
   * @default undefined
   */
  "data-testid"?: string;
};

/**
 * Component | Button
 *
 * Renders a styled button with support for primary, secondary, and tertiary variants.
 * Supports loading state with spinner, optional icon placement, and fluid width.
 *
 * - When `isLoading` is true:
 *   - A spinner is shown on the left
 *   - Icons are hidden, regardless of position
 *
 * @Figma https://www.figma.com/design/uJsnuoosxqn26Rot52zCXZ/%F0%9F%A7%B9-Swing-Design-System-v2?node-id=1236-31990
 */
export function Button(props: ButtonProps) {
  const {
    type = "primary",
    children,
    onClick,
    isFluid = false,
    status = "enabled",
    "data-testid": dataTestId,
  } = props;

  const isLoading = status === "loading";
  const isDisabled = status === "disabled";
  const iconPosition = props.icon?.position ?? "left";
  const isIconOnly = !children && !!props.icon?.name;

  return (
    <button
      data-testid={dataTestId}
      aria-label={children}
      css={{
        ...styles.buttonContainer,
        ...buttonVariantStyles[type],
        ...(isFluid && { width: "100%" }),
        ...(isLoading && {
          cursor: "not-allowed",
          // Override the border color for secondary buttons
          // while an action is pending
          ...(type === "secondary" && {
            borderColor: Colors.blue600,
          }),
        }),
        ...(isDisabled || isLoading ? { userSelect: "none" } : {}),
        // override for when there is no children and just an icon
        ...(isIconOnly && styles.iconOnly),
      }}
      disabled={isDisabled || isLoading}
      onClick={isDisabled || isLoading ? undefined : onClick}
    >
      <span
        css={{
          display: "inline-flex",
          alignItems: "center",
          flexDirection: isLoading ? "row" : iconPosition === "right" ? "row-reverse" : "row",
          gap: children && (!!props.icon?.name || isLoading) ? 8 : 0,
        }}
      >
        {/* Spinner takes priority over icon - it is always rendered on the left */}
        {isLoading ? (
          <Spinner
            color={type === "secondary" || type === "tertiary" ? Colors.blue600 : "currentColor"}
          />
        ) : props.icon?.name ? (
          <Icon name={props.icon.name} color="currentColor" />
        ) : null}

        {/* Children */}
        {children && (
          <span
            css={{
              textAlign: "center",
              ...fontMedium({
                fontWeight: 600,
                color:
                  isLoading && (type === "secondary" || type === "tertiary")
                    ? "blue600"
                    : undefined, // will fall back to "inherit"
              }),
            }}
          >
            {children}
          </span>
        )}
      </span>
    </button>
  );
}

/********* Helper Component ********/
type SpinnerProps = {
  color: ValuesOf<typeof Colors> | "currentColor";
};

// Rotates the entire progress icon continuously
const rotate = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

export function Spinner(props: SpinnerProps) {
  const { color } = props;
  return (
    <div
      css={{
        display: "inline-block",
        animation: `${rotate} 1.5s linear infinite`,
      }}
    >
      <Icon name="Progress" color={color} />
    </div>
  );
}

// TODO: Add a focus state for the button based on the design system
// not currently being prioritized right now.

/********* Styles ********/
const styles = {
  buttonContainer: {
    display: "flex",
    width: "fit-content",
    // NOTE: Using `6px` vs `8px` top and bottom padding since the 2px border
    // results in `8px` top and bottom padding. This approach is also done for
    // the left and right padding.
    padding: "6px 14px",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    borderRadius: 30,
    minWidth: 110,
  },
  primary: {
    backgroundColor: Colors.blue500,
    color: Colors.white200,
    border: `2px solid ${Colors.blue500}`,
    boxShadow: BoxShadows.default,
    "&:hover": {
      backgroundColor: Colors.blue600,
      border: `2px solid ${Colors.blue600}`,
    },
    "&:disabled": {
      backgroundColor: Colors.slate200,
      border: `2px solid ${Colors.slate200}`,
      color: Colors.slate400,
      cursor: "not-allowed",
    },
  },
  secondary: {
    backgroundColor: "transparent",
    color: Colors.blue500,
    border: `2px solid ${Colors.blue500}`,
    "&:hover": {
      color: Colors.blue600,
      borderColor: Colors.blue600,
    },
    "&:disabled": {
      color: Colors.slate400,
      borderColor: Colors.slate400,
      cursor: "not-allowed",
    },
  },
  tertiary: {
    backgroundColor: "transparent",
    color: Colors.blue500,
    border: "2px solid transparent",
    "&:hover": {
      color: Colors.blue600,
    },
    "&:disabled": {
      color: Colors.slate400,
      cursor: "not-allowed",
    },
  },
  iconOnly: {
    border: "none",
    backgroundColor: "transparent",
    boxShadow: "none",
    color: Colors.blue500,
    minWidth: "auto", // Reset the minWidth when in icon only variant
    "&:hover": {
      color: Colors.blue600,
    },
    "&:disabled": {
      color: Colors.slate400,
      cursor: "not-allowed",
    },
  },
} satisfies Record<string, CSSObject>;

const buttonVariantStyles = {
  primary: styles.primary,
  secondary: styles.secondary,
  tertiary: styles.tertiary,
} satisfies Record<Required<ButtonProps>["type"], CSSObject>;
